import { request } from 'api';
import { endpoints } from 'api/endpoints';
import { IconApplication, NoOrdersIcon } from 'assets/icons';
import { FilterBlock } from 'components/FiltersBlock';
import { ExecutorTypeEnum } from 'enums/executorType';
import { useAppSelector } from 'hooks/useAppSelector';
import useMediaQuery from 'hooks/useMediaQuery';
import { usePrevious } from 'hooks/usePrevious';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { ordersActions } from 'reduxStore/reducers/OrdersSlice';
import { getOrdersFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getFilters';
import { getExecutorType } from 'reduxStore/reducers/ProfileSlice/selectors/getExecutorType';
import { OrderByFilterRequest, OrderByFilterResponse, OrderForListCard } from 'types';
import { Spinner } from 'ui/Spinner';
import { OrderCardMobile } from 'ui/redesign/OrderCard';
import { PageWrapper } from 'ui/redesign/PageWrapper';
import { Table } from 'ui/redesign/Table';
import { TableCard } from 'ui/redesign/Table/TableCard';
import { TableLoading } from 'ui/redesign/Table/TableLoading';
import { getFilter } from 'utils/helpers';
import { IS_SMALL_DEVICE } from 'utils/settings';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { NoneOrders } from './NoneOrders';
import { OrderFiltersModal } from './OrdersFiltersModal';
import { OrdersTabs } from './OrdersTabs';
import { ordersTableHeadings, subcontractorOrdersTableHeadings } from './constants';
import { OrderTypesEnum } from './enums';
import { determinateOrdersFiltersCount } from './utils';
import { PrimaryCityBanner } from 'components/redesign/PrimaryCityBanner';
import { ProfileService } from 'services/ProfileService';
import { profileActions } from 'reduxStore/reducers/ProfileSlice';
import { AlertBannerBlock } from 'components/redesign/AlertBannerBlock';
import AlertBannertImg from 'assets/images/alert-banner.png';

const fetchOrders = async (filter: OrderByFilterRequest) => {
  const response = await request<OrderByFilterResponse>(
    endpoints.GET_ORDERS.type,
    endpoints.GET_ORDERS.url(),
    {
      data: JSON.stringify(filter),
      responseType: 'json',
    }
  );
  function filterUniqueById(items: OrderForListCard[]): OrderForListCard[] {
    const uniqueItems: OrderForListCard[] = [];
    const seenIds = new Set<number>();
    for (const item of items) {
      if (!seenIds.has(item.id)) {
        uniqueItems.push(item);
        seenIds.add(item.id);
      }
    }
    return uniqueItems;
  }
  const filterdOrders = filterUniqueById(response.data.pagedList);
  const filteredResponse = {
    ...response.data,
    pagedList: filterdOrders,
    totalCount: filterdOrders?.length,
  };
  return filteredResponse;
};
const OrdersPage = () => {
  const executorType = useAppSelector(getExecutorType);
  const filters = useAppSelector(getOrdersFilters);
  const orderType =
    executorType === ExecutorTypeEnum.Subcontractor
      ? OrderTypesEnum.inProgress
      : OrderTypesEnum.new;
  const { type = orderType } = useParams<{ type: OrderTypesEnum }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const prevOrderTabType = usePrevious(type);
  const [enabled, setEnabled] = useState(true);
  const isSmall = useMediaQuery(IS_SMALL_DEVICE);
  const [ordersCount, setOrdersCount] = useState<Record<OrderTypesEnum, number>>(
    {} as Record<OrderTypesEnum, number>
  );

  const { data: profileData } = useQuery(
    ['getProfileForBanner'],
    () => ProfileService.getProfile(),
    {
      onSuccess: async (data) => {
        dispatch(profileActions.setProfile(data.data));
      },
      enabled: true,
    }
  );

  const filteredOrdersTableHeadings =
    // скрываем для ГПХ лишние столбцы
    executorType === ExecutorTypeEnum.DRITS
      ? Object.fromEntries(
          Object.entries(ordersTableHeadings).map(([key, value]) => [
            key,
            value.filter(
              (item: any) => item.name !== 'Стоимость' && item.name !== 'Предварительная стоимость'
            ),
          ])
        )
      : ordersTableHeadings;

  useEffect(() => {
    window.scrollTo(0, 0);
    setEnabled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleRowClick = (tableRow: TableData): void => {
    navigate(`/orders/${type}/${tableRow.id}`);
  };

  const { isFetching, data, refetch } = useQuery(
    type,
    () => {
      return fetchOrders(getFilter(type, filters, 1));
    },
    {
      enabled,
      onSuccess: (data) => {
        setEnabled(false);
        setOrdersCount((prevState) => {
          return {
            ...prevState,
            [type]: data?.totalCount,
          };
        });
      },
      onError: () => {
        setEnabled(false);
      },
    }
  );

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const totalNewOrders = await fetchOrders(getFilter('new', filters, 1));
        const totalInProgressOrders = await fetchOrders(getFilter('inProgress', filters, 1));
        const totalCompletedOrders = await fetchOrders(getFilter('completed', filters, 1));
        setOrdersCount({
          [OrderTypesEnum.new]: totalNewOrders?.totalCount,
          [OrderTypesEnum.inProgress]: totalInProgressOrders?.totalCount,
          [OrderTypesEnum.completed]: totalCompletedOrders?.totalCount,
        });
      } catch (error) {
        console.log(error);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    setEnabled(true);
    refetch();
  }, [filters]);

  const handleOnFilter = (newFilters: any) => {
    const filterBody = {
      ...newFilters,
    };
    dispatch(ordersActions.setFilters({ ...filters, ...filterBody }));
    setEnabled(true);
  };

  const handleOpenFilterModal = (): void => {
    dispatch(
      modalActions.handleOpenModal({
        content: (
          <OrderFiltersModal
            prevFilters={filters}
            onFilter={handleOnFilter}
            orderTabType={prevOrderTabType ? prevOrderTabType : OrderTypesEnum['new']}
          />
        ),
        options: {
          withCloseButton: false,
          modalClassName: 'w-full sm:w-[650px] !overflow-visible',
          contentClassName: '!overflow-visible',
        },
      })
    );
  };

  if (!Object.keys(OrderTypesEnum).includes(type)) return <Navigate to="/orders/new" />;

  return (
    <PageWrapper title="Заявки" isGoBack={false}>
      {profileData?.data.executorType === null && (
        <AlertBannerBlock
          icon={<img src={AlertBannertImg} alt="" />}
          title={
            <p className="mt-6">Для получения доступа к заявкам, пожалуйста, заполните анкету</p>
          }
          buttonName="Перейти в анкету"
          onClick={() => navigate('/moderation-request')}
        />
      )}
      <section className="flex flex-col gap-6">
        <PrimaryCityBanner />
        {isFetching ? (
          <Spinner />
        ) : (
          <OrdersTabs ordersCounts={ordersCount} isFetching={isFetching} activeType={type} />
        )}
        {isFetching ? (
          <TableLoading />
        ) : (
          <>
            {profileData?.data.executorType === null ? (
              <></>
            ) : data?.totalCount === 0 ? (
              <div className="min-h-[90vh] overflow-hidden rounded-[10px] bg-base shadow-3xl">
                <FilterBlock
                  countFilters={determinateOrdersFiltersCount(filters)}
                  onFilterButtonClick={handleOpenFilterModal}
                />
                {type === OrderTypesEnum.new ? (
                  <div className="max-w-[550px] sm:p-4">
                    <NoneOrders
                      icon={
                        determinateOrdersFiltersCount(filters) ? (
                          <IconApplication />
                        ) : (
                          <NoOrdersIcon />
                        )
                      }
                      heading={
                        determinateOrdersFiltersCount(filters)
                          ? 'Заявки не найдены'
                          : 'В вашем населенном пункте заявки отсутствуют'
                      }
                      description={
                        determinateOrdersFiltersCount(filters)
                          ? 'Измените настройки фильтра и попробуйте еще раз'
                          : 'Попробуйте выбрать другой населенный пункт'
                      }
                      onButtonClick={
                        determinateOrdersFiltersCount(filters)
                          ? undefined
                          : () => navigate(`/profile/private`)
                      }
                    />
                  </div>
                ) : type === OrderTypesEnum.inProgress ? (
                  <div className="max-w-[550px] sm:p-4">
                    <NoneOrders
                      icon={<IconApplication />}
                      heading={
                        determinateOrdersFiltersCount(filters)
                          ? 'Заявки не найдены'
                          : 'Заявок в работе нет'
                      }
                      description={
                        determinateOrdersFiltersCount(filters)
                          ? 'Измените настройки фильтра и попробуйте еще раз'
                          : executorType === ExecutorTypeEnum.Subcontractor
                          ? ''
                          : 'Для поиска заявок, пожалуйста, воспользуйтесь разделом Новые'
                      }
                      onButtonClick={
                        determinateOrdersFiltersCount(filters)
                          ? undefined
                          : executorType === ExecutorTypeEnum.Subcontractor
                          ? undefined
                          : () => navigate('/orders/new')
                      }
                    />
                  </div>
                ) : (
                  <div className="max-w-[550px] sm:p-4">
                    <NoneOrders
                      icon={<IconApplication />}
                      heading="Заявки не найдены"
                      description={
                        determinateOrdersFiltersCount(filters)
                          ? 'Измените настройки фильтра и попробуйте еще раз'
                          : ''
                      }
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                {isSmall ? (
                  <>
                    <FilterBlock
                      countFilters={determinateOrdersFiltersCount(filters)}
                      onFilterButtonClick={handleOpenFilterModal}
                    />
                    {data?.pagedList &&
                      data?.pagedList.map((order: any) => (
                        <OrderCardMobile key={order.id} order={order} />
                      ))}
                  </>
                ) : (
                  <TableCard>
                    <FilterBlock
                      countFilters={determinateOrdersFiltersCount(filters)}
                      onFilterButtonClick={handleOpenFilterModal}
                    />
                    <Table
                      tableHeading={
                        executorType === ExecutorTypeEnum.Subcontractor
                          ? subcontractorOrdersTableHeadings[type]
                          : filteredOrdersTableHeadings[type]
                      }
                      tableData={(data?.pagedList || []) as unknown as TableData[]}
                      onRowClick={handleRowClick}
                    />
                  </TableCard>
                )}
              </>
            )}
          </>
        )}
      </section>
    </PageWrapper>
  );
};

export default OrdersPage;
